/**
 * Deadlines live in two shapes. A template carries a duration, because it has no calendar.
 * A board task carries the date that duration resolved to when the task became active.
 */
export type Deadline = {
  /** Working days or calendar days from activation. */
  days: number;
  /** Skip Saturdays and Sundays. Public holidays are not handled. */
  businessDays?: boolean;
};

export type DueState = "none" | "upcoming" | "dueToday" | "overdue";

export const addDays = (from: Date, days: number, businessDays = false): Date => {
  const result = new Date(from);

  if (!businessDays) {
    result.setDate(result.getDate() + days);
    return result;
  }

  let remaining = days;

  while (remaining > 0) {
    result.setDate(result.getDate() + 1);
    const weekday = result.getDay();
    if (weekday !== 0 && weekday !== 6) remaining--;
  }

  return result;
};

/** Whole days between two dates, compared by date rather than timestamp. */
export const daysBetween = (from: Date, to: Date): number => {
  const a = Date.UTC(from.getFullYear(), from.getMonth(), from.getDate());
  const b = Date.UTC(to.getFullYear(), to.getMonth(), to.getDate());
  return Math.round((b - a) / 86_400_000);
};

/**
 * A deadline only means something while the work is outstanding — a task completed two
 * days late is not "overdue", it is done.
 *
 * @param {string | Date} [dueAt] - The resolved due date, when the task has one.
 * @param {boolean} [resolved] - Whether the task has already finished.
 * @returns {{ state: DueState, days: number }} days is negative once overdue.
 */
export const dueState = (dueAt?: string | Date, resolved = false): { state: DueState; days: number } => {
  if (!dueAt || resolved) return { state: "none", days: 0 };

  const days = daysBetween(new Date(), new Date(dueAt));

  if (days < 0) return { state: "overdue", days };
  if (days === 0) return { state: "dueToday", days };
  return { state: "upcoming", days };
};

export const describeDeadline = (deadline?: Deadline) =>
  !deadline
    ? "No deadline"
    : `${deadline.days} ${deadline.businessDays ? "working" : "calendar"} day${deadline.days === 1 ? "" : "s"} after it starts`;

export const describeDue = (dueAt?: string | Date, resolved = false) => {
  const { state, days } = dueState(dueAt, resolved);

  if (state === "none") return "";
  if (state === "overdue") return `${Math.abs(days)}d overdue`;
  if (state === "dueToday") return "Due today";
  return `Due in ${days}d`;
};
